import type {
  propsAmortizationType,
  propsContractFrequency,
  propsContractIndexer,
  propsContractModality,
  RiskLevel,
} from '~/store/contracts/interfaces';

const MODALITY_LABELS: Record<propsContractModality, string> = {
  custeio: 'Custeio',
  investimento: 'Investimento',
  capital_giro: 'Capital de Giro',
  cprf: 'CPRF',
  comercializacao: 'Comercialização',
  outros: 'Outros',
};

const AMORTIZATION_LABELS: Record<propsAmortizationType, string> = {
  price: 'Price',
  sac: 'SAC',
  bullet: 'Pagamento único',
};

const FREQUENCY_LABELS: Record<propsContractFrequency, string> = {
  monthly: 'Mensal',
  semiannual: 'Semestral',
  annual: 'Anual',
  harvest: 'Por safra',
};

const INDEXER_LABELS: Record<propsContractIndexer, string> = {
  'pre-fixed': 'Pré-fixado',
  cdi: 'CDI',
  ipca: 'IPCA',
  selic: 'SELIC',
};

const RISK_LABELS: Record<RiskLevel, string> = {
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
};

export const modalityLabel = (m?: propsContractModality) =>
  (m && MODALITY_LABELS[m]) || 'Outros';

export const amortizationLabel = (a?: propsAmortizationType) =>
  (a && AMORTIZATION_LABELS[a]) || '—';

export const frequencyLabel = (f?: propsContractFrequency) =>
  (f && FREQUENCY_LABELS[f]) || '—';

export const indexerLabel = (i?: propsContractIndexer) =>
  (i && INDEXER_LABELS[i]) || '—';

export const riskLabel = (r?: RiskLevel) => (r && RISK_LABELS[r]) || '—';

const MONTH_NAMES = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

export const formatDateLong = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

export const formatDateShort = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getUTCDate()).padStart(2, '0')} ${
    MONTH_NAMES[d.getUTCMonth()]
  } ${d.getUTCFullYear()}`;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const dayIndex = (year: number, month: number, day: number) =>
  Math.floor(Date.UTC(year, month, day) / DAY_MS);

export const relativeDueLabel = (iso?: string): string => {
  if (!iso) return '—';
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return '—';
  const now = new Date();
  const diff =
    dayIndex(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate()) -
    dayIndex(now.getFullYear(), now.getMonth(), now.getDate());
  if (diff < -1) return `Vencido há ${Math.abs(diff)} dias`;
  if (diff === -1) return 'Vencido ontem';
  if (diff === 0) return 'Vence hoje';
  if (diff === 1) return 'Vence amanhã';
  if (diff <= 45) return `Vence em ${diff} dias`;
  const months = Math.round(diff / 30);
  return `Vence em ${months} ${months === 1 ? 'mês' : 'meses'}`;
};

export const formatRatePct = (rate?: number) => {
  if (rate === undefined || rate === null) return '—';
  const safe = Number.isFinite(rate) ? rate : 0;
  return `${(safe * 100).toFixed(2).replace('.', ',')}% a.a.`;
};

export const parseDateBRToISO = (br?: string): string | undefined => {
  if (!br) return undefined;
  const digits = br.replace(/\D/g, '');
  if (digits.length !== 8) return undefined;
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  const iso = `${year}-${month}-${day}T00:00:00.000Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : iso;
};

export const parseAmountToCents = (raw?: string): number => {
  if (!raw) return 0;
  const cleaned = raw.replace(/\D/g, '');
  if (!cleaned) return 0;
  return Number(cleaned);
};

export const parsePercentToRate = (raw?: string): number => {
  if (!raw) return 0;
  const cleaned = raw.replace(/[^\d.,]/g, '').replace(',', '.');
  const num = Number.parseFloat(cleaned);
  if (!Number.isFinite(num)) return 0;
  return num / 100;
};
